import {
  combineReducers,
  configureStore,
  type StoreEnhancer,
} from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';
import { enableMapSet } from 'immer';
import { persistReducer, persistStore } from 'redux-persist';

import { isReduxPersistEnabled, persistConfig } from '~/dev/reduxPersist';
import { isDev } from '~/dev/utils/isDev';
import { subscribeDynamicDataExtractionPersistence } from '~/features/dynamic-data-extraction/utils/subscribeDynamicDataExtractionPersistence';
import { atlas } from '~/redux/api/atlas';
import { api } from '~/redux/api/createApi';
import { esign } from '~/redux/api/esign';
import { evisearch } from '~/redux/api/evisearch';
import { hermes } from '~/redux/api/hermes';
import { k2 } from '~/redux/api/k2';
import { managedEntities } from '~/redux/api/managed-entities';
import { rolodex } from '~/redux/api/rolodex';
import { unifiedFields } from '~/redux/api/unified-fields';
import { weaver } from '~/redux/api/weaver';
import { apiCallAnalyticsMiddleware } from '~/redux/middleware/apiCallAnalyticsMiddleware';
import { invalidateFolderContentOnUploadMiddleware } from '~/redux/middleware/invalidateFolderContentOnUpload';
import { reducer } from '~/redux/slices';

enableMapSet();

/**
 * Persistence is only applied in local dev AND only when an engineer has
 * opted in via the devtools panel (opt-in: off by default). In every other
 * case the plain combined reducer is used, so production is unchanged. This is
 * read once at init, so toggling the preference requires a reload.
 */
const persistenceActive = isDev && isReduxPersistEnabled();

/**
 * The reducer map is combined into a single root reducer so it can be wrapped
 * with redux-persist.
 *
 * The cast keeps the store's state type identical to the non-persisted reducer
 * — redux-persist only adds an internal `_persist` key we never read — so
 * RootState / useAppSelector stay exactly the same.
 */
const combinedReducer = combineReducers(reducer);
const rootReducer = persistenceActive
  ? (persistReducer(
      persistConfig,
      combinedReducer,
    ) as unknown as typeof combinedReducer)
  : combinedReducer;

type PreloadedState = Partial<ReturnType<typeof combinedReducer>> | undefined;

/**
 * Builds an isolated app store with the same middleware/enhancer setup as
 * production. The production singleton (default export) calls this once.
 *
 * Test helpers (~/utils/test) call this with a preloadedState to get fresh,
 * isolated stores per test. Production code should NOT call this — import
 * the default export instead.
 *
 * The middleware factory is inline (not extracted) so configureStore's `S`
 * generic stays inferred from `reducer`. Extracting it widens `S` to
 * `unknown`, which propagates through getState() into RootState and breaks
 * every useAppSelector call site.
 */
export function createAppStore(preloadedState?: PreloadedState) {
  return configureStore({
    reducer: rootReducer,
    middleware: (gdm) =>
      gdm({
        serializableCheck: false,
        immutableCheck: false,
      }).concat(
        invalidateFolderContentOnUploadMiddleware,
        apiCallAnalyticsMiddleware,
        api.middleware,
        k2.middleware,
        unifiedFields.middleware,
        esign.middleware,
        evisearch.middleware,
        managedEntities.middleware,
        hermes.middleware,
        rolodex.middleware,
        weaver.middleware,
        atlas.middleware,
      ),
    devTools: isDev,
    // Cast required: Sentry's StoreEnhancer uses a different redux type import path than RTK
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers({
        // The default `raf` autobatch scheduler queues a `requestAnimationFrame`
        // plus a `setTimeout` fallback. In the happy-dom test env raf never
        // fires, so the fallback timer runs its notify callback after the store
        // and its subscribers have been torn down, throwing an uncaught
        // exception that fails the run. A microtask-based scheduler flushes
        // within the test's own tick, so nothing leaks past teardown. Real
        // browsers fire raf, so production keeps the default.
        autoBatch:
          process.env.NODE_ENV === 'test' ? { type: 'tick' } : undefined,
      }).concat(Sentry.createReduxEnhancer() as StoreEnhancer),
    preloadedState,
  });
}

const store = createAppStore();

subscribeDynamicDataExtractionPersistence(store);

setupListeners(store.dispatch);

/**
 * redux-persist persistor. Only created when persistence is active (local dev,
 * not opted out); `undefined` everywhere else. Consumed by <PersistGate> in the
 * app entrypoint.
 */
export const persistor = persistenceActive ? persistStore(store) : undefined;

export default store;
